import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelText as _vModelText, unref as _unref, createVNode as _createVNode, createCommentVNode as _createCommentVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-73c9ab3e"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "ats-score-tester" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h1", null, "ATS Score Tester", -1))
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", { class: "description" }, "Test how your resume scores against job requirements", -1))
const _hoisted_4 = { class: "form-container" }
const _hoisted_5 = { class: "form-section" }
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", null, "Resume Selection", -1))
const _hoisted_7 = { class: "form-group" }
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", { for: "resume-select" }, "Select Resume", -1))
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("option", { value: "" }, "-- Select a Resume --", -1))
const _hoisted_10 = ["value"]
const _hoisted_11 = { class: "form-group" }
const _hoisted_12 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", { for: "application-select" }, "Select Application", -1))
const _hoisted_13 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("option", { value: "" }, "-- Select an Application --", -1))
const _hoisted_14 = ["value"]
const _hoisted_15 = { class: "form-actions" }
const _hoisted_16 = ["disabled"]
const _hoisted_17 = { class: "form-section" }
const _hoisted_18 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", null, "Manual Job Requirements", -1))
const _hoisted_19 = { class: "form-group" }
const _hoisted_20 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", { for: "job-title" }, "Job Title", -1))
const _hoisted_21 = { class: "form-group" }
const _hoisted_22 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", { for: "required-experience" }, "Required Experience", -1))
const _hoisted_23 = { class: "form-group" }
const _hoisted_24 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", { for: "required-skills" }, "Required Skills (comma separated)", -1))
const _hoisted_25 = { class: "form-group" }
const _hoisted_26 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", null, "Keywords with Weights", -1))
const _hoisted_27 = { class: "keywords-container" }
const _hoisted_28 = ["onUpdate:modelValue"]
const _hoisted_29 = ["onUpdate:modelValue"]
const _hoisted_30 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("option", { value: "10" }, "Critical (10)", -1))
const _hoisted_31 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("option", { value: "5" }, "Important (5)", -1))
const _hoisted_32 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("option", { value: "2" }, "Nice-to-have (2)", -1))
const _hoisted_33 = [
  _hoisted_30,
  _hoisted_31,
  _hoisted_32
]
const _hoisted_34 = ["onClick"]
const _hoisted_35 = { class: "form-actions" }
const _hoisted_36 = ["disabled"]
const _hoisted_37 = {
  key: 0,
  class: "results-container"
}

import { ref, computed, onMounted, defineAsyncComponent } from "vue";
import { DocumentApiService } from "../services/DocumentApiService";
import { ApplicationApiService } from "../services/ApplicationApiService";

// Import using defineAsyncComponent to handle the component without default export

export default /*#__PURE__*/_defineComponent({
  __name: 'ATSScoreTester',
  setup(__props) {

const ATSScorePanel = defineAsyncComponent(() => import("./resume-editor/ATSScorePanel.vue"));

// State
const resumes = ref<Array<{ id: string; name: string }>>([]);
const applications = ref<Array<{ id: string; roleTitle: string; companyName: string }>>([]);
const selectedResumeId = ref("");
const selectedApplicationId = ref("");
const jobTitle = ref("");
const requiredExperience = ref("");
const requiredSkillsText = ref("");
const keywords = ref<Array<{ keyword: string; weight: number }>>([{ keyword: "", weight: 10 }]);
const scorePanel = ref<any>(null); // Use any type to avoid TypeScript errors with the ref

// Computed properties
const requiredSkills = computed(() => {
  return requiredSkillsText.value
    .split(",")
    .map((skill) => skill.trim())
    .filter((skill) => skill !== "");
});

// Methods
function addKeyword() {
  keywords.value.push({ keyword: "", weight: 5 });
}

function removeKeyword(index: number) {
  keywords.value.splice(index, 1);

  // Always keep at least one keyword field
  if (keywords.value.length === 0) {
    addKeyword();
  }
}

function analyzeResume() {
  if (scorePanel.value) {
    scorePanel.value.scoreResume();
  }
}

function quickAnalyzeResume() {
  if (scorePanel.value) {
    scorePanel.value.quickScoreResume();
  }
}

// Fetch user's resumes on component mount
async function fetchResumes() {
  try {
    const response = await DocumentApiService.getUserResumes();
    resumes.value = response.data.map((resume) => ({
      ...resume,
      id: resume.id || crypto.randomUUID(),
      name: resume.name || "Untitled Resume",
      lastModified: resume.lastModified ? new Date(resume.lastModified) : new Date(),
      previewUrl: resume.previewUrl || null,
    }));
  } catch (error) {
    console.error("Error fetching resumes:", error);
  }
}

// Fetch user's applications
async function fetchApplications() {
  try {
    const response = await ApplicationApiService.getUserApplicationList();
    applications.value = response.data.applications.map((application: any) => ({
      id: application.applicationId,
      roleTitle: application.roleTitle || "Untitled Position",
      companyName: application.companyName || "Unknown Company",
    }));
  } catch (error) {
    console.error("Error fetching applications:", error);
  }
}

onMounted(() => {
  fetchResumes();
  fetchApplications();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _hoisted_3,
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _hoisted_6,
        _createElementVNode("div", _hoisted_7, [
          _hoisted_8,
          _withDirectives(_createElementVNode("select", {
            id: "resume-select",
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedResumeId).value = $event)),
            class: "form-control"
          }, [
            _hoisted_9,
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(resumes.value, (resume) => {
              return (_openBlock(), _createElementBlock("option", {
                key: resume.id,
                value: resume.id
              }, _toDisplayString(resume.name), 9, _hoisted_10))
            }), 128))
          ], 512), [
            [_vModelSelect, selectedResumeId.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_11, [
          _hoisted_12,
          _withDirectives(_createElementVNode("select", {
            id: "application-select",
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedApplicationId).value = $event)),
            class: "form-control"
          }, [
            _hoisted_13,
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(applications.value, (application) => {
              return (_openBlock(), _createElementBlock("option", {
                key: application.id,
                value: application.id
              }, _toDisplayString(application.roleTitle) + " at " + _toDisplayString(application.companyName), 9, _hoisted_14))
            }), 128))
          ], 512), [
            [_vModelSelect, selectedApplicationId.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_15, [
          _createElementVNode("button", {
            onClick: quickAnalyzeResume,
            class: "btn-analyze",
            disabled: !selectedResumeId.value || !selectedApplicationId.value
          }, " Quick Analyze with Application ", 8, _hoisted_16)
        ])
      ]),
      _createElementVNode("div", _hoisted_17, [
        _hoisted_18,
        _createElementVNode("div", _hoisted_19, [
          _hoisted_20,
          _withDirectives(_createElementVNode("input", {
            id: "job-title",
            type: "text",
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((jobTitle).value = $event)),
            class: "form-control",
            placeholder: "e.g., Senior Software Engineer"
          }, null, 512), [
            [_vModelText, jobTitle.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_21, [
          _hoisted_22,
          _withDirectives(_createElementVNode("input", {
            id: "required-experience",
            type: "text",
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((requiredExperience).value = $event)),
            class: "form-control",
            placeholder: "e.g., 5+ years"
          }, null, 512), [
            [_vModelText, requiredExperience.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_23, [
          _hoisted_24,
          _withDirectives(_createElementVNode("textarea", {
            id: "required-skills",
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((requiredSkillsText).value = $event)),
            class: "form-control",
            placeholder: "e.g., JavaScript, React, Node.js",
            rows: "3"
          }, null, 512), [
            [_vModelText, requiredSkillsText.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_25, [
          _hoisted_26,
          _createElementVNode("div", _hoisted_27, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(keywords.value, (keyword, index) => {
              return (_openBlock(), _createElementBlock("div", {
                key: index,
                class: "keyword-item"
              }, [
                _withDirectives(_createElementVNode("input", {
                  type: "text",
                  "onUpdate:modelValue": ($event: any) => ((keyword.keyword) = $event),
                  class: "form-control keyword-input",
                  placeholder: "Keyword"
                }, null, 8, _hoisted_28), [
                  [_vModelText, keyword.keyword]
                ]),
                _withDirectives(_createElementVNode("select", {
                  "onUpdate:modelValue": ($event: any) => ((keyword.weight) = $event),
                  class: "form-control weight-select"
                }, _hoisted_33, 8, _hoisted_29), [
                  [_vModelSelect, keyword.weight]
                ]),
                _createElementVNode("button", {
                  onClick: ($event: any) => (removeKeyword(index)),
                  class: "btn-remove"
                }, "✕", 8, _hoisted_34)
              ]))
            }), 128)),
            _createElementVNode("button", {
              onClick: addKeyword,
              class: "btn-add"
            }, "+ Add Keyword")
          ])
        ]),
        _createElementVNode("div", _hoisted_35, [
          _createElementVNode("button", {
            onClick: analyzeResume,
            class: "btn-analyze",
            disabled: !selectedResumeId.value || !jobTitle.value
          }, " Manual Analyze ", 8, _hoisted_36)
        ])
      ])
    ]),
    (selectedResumeId.value && (selectedApplicationId.value || jobTitle.value))
      ? (_openBlock(), _createElementBlock("div", _hoisted_37, [
          _createVNode(_unref(ATSScorePanel), {
            resumeId: selectedResumeId.value,
            applicationId: selectedApplicationId.value,
            jobTitle: jobTitle.value,
            requiredExperience: requiredExperience.value,
            requiredSkills: requiredSkills.value,
            keywords: keywords.value,
            ref_key: "scorePanel",
            ref: scorePanel
          }, null, 8, ["resumeId", "applicationId", "jobTitle", "requiredExperience", "requiredSkills", "keywords"])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})